import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from 'reactstrap';
import { DOC_TYPES } from './fixture';
import { useCreatePolicyVersion } from './usePolicy';
import { isApiError } from '../../api/errors';

export function CreatePolicyVersionModal({ isOpen, defaultDocType, onClose }) {
  const [documentType, setDocumentType] = useState(defaultDocType ?? 'agb');
  const [version, setVersion] = useState(suggestVersion());
  const [bodyUrl, setBodyUrl] = useState('');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [error, setError] = useState(null);
  const create = useCreatePolicyVersion();

  const reset = () => {
    setDocumentType(defaultDocType ?? 'agb');
    setVersion(suggestVersion());
    setBodyUrl('');
    setEffectiveAt('');
    setError(null);
  };
  const finish = () => {
    reset();
    onClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!version.trim()) {
      setError('Version-String ist Pflicht.');
      return;
    }
    try {
      await create.mutateAsync({
        documentType,
        version: version.trim(),
        bodyUrl: bodyUrl.trim() || undefined,
        effectiveAt: effectiveAt ? new Date(effectiveAt).toISOString() : undefined,
      });
      finish();
    } catch (err) {
      if (isApiError(err) && (err.code === 'ER_DUP_ENTRY' || err.status === 409)) {
        setError(`Version "${version}" für ${documentType} existiert bereits. Erhöhe die Versionsnummer.`);
      } else {
        setError(isApiError(err) ? err.message : err?.message ?? 'Anlegen fehlgeschlagen.');
      }
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={create.isPending ? undefined : finish} backdrop="static">
      <ModalHeader toggle={create.isPending ? undefined : finish}>Neue Version anlegen</ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>Doc-Type</Label>
            <Input type="select" value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
              {DOC_TYPES.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Version-String *</Label>
            <Input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="z. B. 2026-v1"
              maxLength={32}
              required
            />
            <small className="text-muted">Pattern-Empfehlung: <code>JAHR-vN</code>. Muss unique sein pro (Brand, Doc-Type).</small>
          </FormGroup>
          <FormGroup>
            <Label>Body-URL <span className="text-muted small">(optional)</span></Label>
            <Input
              type="url"
              value={bodyUrl}
              onChange={(e) => setBodyUrl(e.target.value)}
              placeholder="https://example.com/legal/agb/2026-v1.pdf"
            />
          </FormGroup>
          <FormGroup>
            <Label>Wirksam ab <span className="text-muted small">(leer = sofort)</span></Label>
            <Input
              type="datetime-local"
              value={effectiveAt}
              onChange={(e) => setEffectiveAt(e.target.value)}
            />
            <small className="text-muted">
              Lokale Browser-Zeit, wird in UTC gespeichert. Leer lassen für „sofort wirksam".
            </small>
          </FormGroup>
          {error && <Alert color="danger">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={finish} disabled={create.isPending}>Abbrechen</Button>
          <Button color="primary" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Anlegen…' : 'Anlegen'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function suggestVersion() {
  const y = new Date().getFullYear();
  return `${y}-v1`;
}
